import { useState } from "react";
import { api } from "@/lib/api";
import { X, Loader2, Calculator, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BASES = [
    { v: "cost", l: "Preço de Custo" },
    { v: "pvr", l: "PVR (Preço Recomendado)" },
    { v: "current", l: "Preço actual" },
];

export default function BulkMarginModal({ brands, categories, defaultBrand, defaultCategory, onClose, onApplied }) {
    const [form, setForm] = useState({
        brand: defaultBrand || "",
        category: defaultCategory || "",
        base: "cost",
        margin_type: "percent",
        margin_value: 35,
        respect_flags: true,
        respect_disabled: true,
    });
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    const upd = (k) => (e) =>
        setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const runDry = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/products/bulk-margin", { ...form, margin_value: Number(form.margin_value), dry_run: true });
            setPreview(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao simular");
        } finally { setBusy(false); }
    };
    const runApply = async () => {
        if (!preview) return;
        if (!window.confirm(`Vai actualizar ${preview.would_update} produtos. Confirmar?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/products/bulk-margin", { ...form, margin_value: Number(form.margin_value), dry_run: false });
            toast.success(`${data.updated} produtos actualizados ✓`, { duration: 6000 });
            onApplied();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="bulk-margin-modal">
            <div className="bg-white border-2 border-ink w-full max-w-3xl shadow-[8px_8px_0_0_#0A0A0A] my-10">
                <div className="p-5 border-b-2 border-ink flex justify-between items-center">
                    <h2 className="font-display font-black text-2xl flex items-center gap-2">
                        <Calculator size={22} /> Aplicar margem em massa
                    </h2>
                    <button onClick={onClose}><X size={20} /></button>
                </div>

                <div className="p-6 space-y-5">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="label-mono mb-2 block">Marca (vazio = todas)</span>
                            <select value={form.brand} onChange={upd("brand")} className="input-edge" data-testid="bulk-brand">
                                <option value="">Todas as marcas</option>
                                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="label-mono mb-2 block">Categoria (vazio = todas)</span>
                            <select value={form.category} onChange={upd("category")} className="input-edge" data-testid="bulk-category">
                                <option value="">Todas as categorias</option>
                                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                            </select>
                        </label>
                    </div>

                    <div className="border-2 border-ink p-4 bg-paper">
                        <div className="label-mono mb-3 text-magenta">Cálculo</div>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <label className="block">
                                <span className="label-mono mb-2 block">Aplicar sobre</span>
                                <select value={form.base} onChange={upd("base")} className="input-edge" data-testid="bulk-base">
                                    {BASES.map((b) => <option key={b.v} value={b.v}>{b.l}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className="label-mono mb-2 block">Tipo</span>
                                <select value={form.margin_type} onChange={upd("margin_type")} className="input-edge" data-testid="bulk-margin-type">
                                    <option value="percent">Percentagem (+%)</option>
                                    <option value="euro">Valor fixo (+€)</option>
                                </select>
                            </label>
                            <label className="block">
                                <span className="label-mono mb-2 block">
                                    {form.margin_type === "percent" ? "Margem (%)" : "Valor (€)"}
                                </span>
                                <input type="number" step="0.01" value={form.margin_value} onChange={upd("margin_value")} className="input-edge" data-testid="bulk-margin-value" />
                            </label>
                        </div>
                        <div className="text-xs font-mono text-ink/60 mt-3 bg-white border-2 border-ink/20 p-2">
                            Fórmula: <strong>Novo preço = {BASES.find((b) => b.v === form.base).l}
                                {form.margin_type === "percent" ? ` × (1 + ${form.margin_value || 0}/100)` : ` + ${form.margin_value || 0} €`}</strong>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={form.respect_flags} onChange={upd("respect_flags")} className="accent-magenta w-4 h-4" />
                            <span className="text-sm font-bold uppercase">Respeitar flags `manual_price` e `sync_no_price`</span>
                        </label>
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={form.respect_disabled} onChange={upd("respect_disabled")} className="accent-magenta w-4 h-4" />
                            <span className="text-sm font-bold uppercase">Saltar produtos desactivados</span>
                        </label>
                    </div>

                    {preview && (
                        <div className="border-2 border-magenta bg-magenta/5 p-4">
                            <div className="label-mono text-magenta mb-2 inline-flex items-center gap-1">
                                <AlertTriangle size={12} /> Pré-visualização
                            </div>
                            <div className="font-mono text-sm grid sm:grid-cols-4 gap-2 mb-3">
                                <div><span className="text-ink/50">Encontrados:</span> <strong>{preview.matched}</strong></div>
                                <div><span className="text-ink/50">A actualizar:</span> <strong className="text-magenta">{preview.would_update}</strong></div>
                                <div><span className="text-ink/50">Sem base:</span> <strong>{preview.skipped_no_base}</strong></div>
                                <div><span className="text-ink/50">Protegidos:</span> <strong>{preview.skipped_flag}</strong></div>
                            </div>
                            {preview.sample.length > 0 && (
                                <table className="w-full text-xs font-mono">
                                    <thead className="border-b-2 border-ink">
                                        <tr>
                                            <th className="text-left py-1">SKU</th>
                                            <th className="text-right">Base</th>
                                            <th className="text-right">Antes</th>
                                            <th className="text-right">Depois</th>
                                            <th className="text-right">Δ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.sample.map((s) => {
                                            const delta = s.new_price - (s.old_price || 0);
                                            return (
                                                <tr key={s.sku} className="border-t border-ink/10">
                                                    <td>{s.sku}</td>
                                                    <td className="text-right">{Number(s.base_value).toFixed(2)} €</td>
                                                    <td className="text-right">{Number(s.old_price || 0).toFixed(2)} €</td>
                                                    <td className="text-right text-magenta font-bold">{Number(s.new_price).toFixed(2)} €</td>
                                                    <td className={`text-right ${delta >= 0 ? "text-accent-green" : "text-destructive"}`}>
                                                        {delta >= 0 ? "+" : ""}{delta.toFixed(2)} €
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    <div className="flex gap-3 justify-end pt-4 border-t-2 border-ink">
                        <button onClick={onClose} className="btn-secondary">Cancelar</button>
                        <button onClick={runDry} disabled={busy} className="btn-secondary" data-testid="bulk-preview-btn">
                            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Simular
                        </button>
                        <button onClick={runApply} disabled={busy || !preview || preview.would_update === 0} className="btn-primary" data-testid="bulk-apply-btn">
                            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Aplicar agora
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
